import React, { useEffect, useState, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { fmtMXN } from "@/lib/format";
import { Check, MessageCircle, Package, Home } from "lucide-react";

export default function CheckoutSuccessPage() {
  const navigate = useNavigate();
  const [status, setStatus] = useState("checking"); // checking | paid | failed | expired
  const [order, setOrder] = useState(null);
  const pollingRef = useRef(0);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get("session_id");
    if (!sessionId) {
      navigate("/");
      return;
    }
    pollStatus(sessionId, 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const pollStatus = async (sessionId, attempt) => {
    const MAX = 8;
    const INTERVAL = 2000;
    if (attempt >= MAX) {
      setStatus("timeout");
      return;
    }
    try {
      const { data } = await api.get(`/checkout/status/${sessionId}`);
      if (data.payment_status === "paid") {
        setStatus("paid");
        setOrder(data.order);
        return;
      }
      if (data.status === "expired") {
        setStatus("expired");
        return;
      }
      pollingRef.current = setTimeout(() => pollStatus(sessionId, attempt + 1), INTERVAL);
    } catch {
      pollingRef.current = setTimeout(() => pollStatus(sessionId, attempt + 1), INTERVAL);
    }
  };

  useEffect(() => {
    return () => clearTimeout(pollingRef.current);
  }, []);

  if (status === "checking") {
    return (
      <div className="min-h-screen bg-arre-black flex items-center justify-center p-6" data-testid="success-checking">
        <div className="text-center max-w-md">
          <div className="w-20 h-20 border-2 border-arre-border border-t-arre-red rounded-full animate-spin mx-auto mb-6" />
          <h1 className="font-display text-4xl tracking-wider uppercase text-white">Confirmando tu pago...</h1>
          <p className="text-white/60 mt-3">Un momento compare, esto puede tardar unos segundos.</p>
        </div>
      </div>
    );
  }

  if (status === "expired" || status === "timeout") {
    return (
      <div className="min-h-screen bg-arre-black flex items-center justify-center p-6">
        <div className="text-center max-w-md">
          <h1 className="font-display text-4xl tracking-wider uppercase text-arre-red mb-3">Pago no confirmado</h1>
          <p className="text-white/70 mb-6">No pudimos confirmar tu pago. Si el cargo aparece en tu tarjeta, contáctanos por WhatsApp con tu número de orden.</p>
          <Link to="/" className="btn-arre bg-arre-red text-white font-display text-lg tracking-widest uppercase px-6 py-3 inline-block">
            Volver al inicio
          </Link>
        </div>
      </div>
    );
  }

  // PAID
  const waMsg = encodeURIComponent(
    `¡Qué onda compare! Hice un pedido en Arre Sonora, aquí va mi número de orden: ${order?.order_number || ""}. Quisiera confirmar el seguimiento.`
  );
  const whatsappUrl = `https://wa.me/526621234567?text=${waMsg}`;

  return (
    <div className="min-h-screen bg-arre-black py-16 px-6" data-testid="success-paid">
      <div className="max-w-3xl mx-auto text-center">
        <div className="w-20 h-20 mx-auto bg-arre-red/20 border border-arre-red flex items-center justify-center mb-6">
          <Check className="w-10 h-10 text-arre-red" strokeWidth={2} />
        </div>
        <div className="text-xs tracking-[0.35em] uppercase text-arre-red mb-3">⟡ ¡Compra confirmada!</div>
        <h1 className="font-display text-5xl md:text-7xl tracking-wider uppercase text-white leading-[0.9]">
          ¡Va con todo,<br /><span className="text-arre-red">compare!</span>
        </h1>
        <p className="font-serif-display italic text-xl text-white/80 mt-6">
          Tu pedido está en camino. Tu carne sonorense se prepara con cariño.
        </p>

        {order && (
          <div className="mt-10 p-8 bg-arre-surface border border-arre-border text-left">
            <div className="flex items-center justify-between flex-wrap gap-4 pb-6 border-b border-arre-border">
              <div>
                <div className="text-xs tracking-widest uppercase text-white/50">Número de orden</div>
                <div className="font-display text-3xl tracking-wider text-white" data-testid="order-number">{order.order_number}</div>
              </div>
              <div>
                <div className="text-xs tracking-widest uppercase text-white/50">Total pagado</div>
                <div className="font-display text-3xl text-white">{fmtMXN(order.total)}</div>
              </div>
            </div>
            <div className="mt-6 space-y-2">
              {order.items.map((it, idx) => (
                <div key={idx} className="flex items-center justify-between text-sm">
                  <span className="text-white/80">{it.name} x{it.quantity}</span>
                  <span className="text-white/60">{fmtMXN(it.line_total)}</span>
                </div>
              ))}
            </div>
            <div className="mt-6 pt-6 border-t border-arre-border text-sm text-white/70">
              <div className="flex justify-between"><span>Subtotal</span><span>{fmtMXN(order.subtotal)}</span></div>
              <div className="flex justify-between"><span>Envío</span><span>{fmtMXN(order.shipping_fee)}</span></div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-10">
          <a
            href={whatsappUrl}
            target="_blank"
            rel="noreferrer"
            className="btn-arre bg-green-600 hover:bg-green-700 text-white font-display text-lg tracking-widest uppercase py-4 px-4 inline-flex items-center justify-center gap-2"
            data-testid="whatsapp-btn"
          >
            <MessageCircle className="w-5 h-5" />
            Seguimiento WhatsApp
          </a>
          <Link
            to="/rastreo"
            className="btn-arre border border-arre-red text-arre-red hover:bg-arre-red hover:text-white font-display text-lg tracking-widest uppercase py-4 px-4 inline-flex items-center justify-center gap-2"
            data-testid="track-btn"
          >
            <Package className="w-5 h-5" />
            Rastrear orden
          </Link>
          <Link
            to="/"
            className="btn-arre border border-arre-border text-white hover:bg-arre-surface font-display text-lg tracking-widest uppercase py-4 px-4 inline-flex items-center justify-center gap-2"
          >
            <Home className="w-5 h-5" />
            Ir al inicio
          </Link>
        </div>
      </div>
    </div>
  );
}
